import React from 'react';
import { useDrop } from 'react-dnd';
import formatDuration from '../../../utils/formatDuration';
import TaskRow, { TASK_DRAG_TYPE, type TaskDragItem } from './TaskRow';
import type { PlanGroup, PlanTask } from '../../../types/plan';

type DayColumnProps = {
  group: PlanGroup;
  date: string;
  isToday: boolean;
  onDropTask: (taskId: number, date: string | null) => void;
  onToggle: (task: PlanTask) => void;
  onEdit: (task: PlanTask) => void;
};

const DayColumn: React.FC<DayColumnProps> = ({
  group,
  date,
  isToday,
  onDropTask,
  onToggle,
  onEdit,
}) => {
  const [{ isOver }, drop] = useDrop<TaskDragItem, unknown, { isOver: boolean }>(
    () => ({
      accept: TASK_DRAG_TYPE,
      drop: (item) => {
        if (item.planned_for !== date) onDropTask(item.id, date);
      },
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [date, onDropTask]
  );

  return (
    <div
      ref={drop as unknown as React.Ref<HTMLDivElement>}
      className={`flex min-h-[240px] flex-col rounded-xl border p-3 transition-colors ${
        isOver ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200 bg-gray-50/60'
      }`}
    >
      <div className="mb-3 flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <span className="text-base font-semibold text-gray-900">
            {isToday ? '今天' : '明天'}
          </span>
          <span className="text-xs text-gray-500">{group.label}</span>
        </div>
        <span className="text-xs text-gray-500">
          {group.stats.completed}/{group.stats.total} 完成 · 预估{' '}
          {formatDuration(group.stats.estimated_minutes)}
        </span>
      </div>

      <div className="flex-1">
        {group.tasks.map((task) => (
          <TaskRow key={task.id} task={task} onToggle={onToggle} onEdit={onEdit} />
        ))}
      </div>

      <div className="mt-2 rounded-lg border border-dashed border-gray-300 py-3 text-center text-xs text-gray-400">
        从 Backlog 拖到这里安排到{isToday ? '今天' : '明天'}
      </div>
    </div>
  );
};

export default DayColumn;
